import { useState } from 'react';
import { useCurrentUser } from '../../../shared/features/session/components/CurrentUserProvider';
import { useToast } from '../../../shared/hooks/use-toast';
import { acknowledgeAlert, reassignAlert, resolveAlert, type AlertView } from '../alerts.api';
import { useAlertFeed } from './use-alert-feed';
import { useRaisedAlertList } from './use-alert-pages';
import { useMinuteClock } from './use-minute-clock';

/**
 * The alert inbox: the list from the URL's query, kept current by the server's stream, and answering an alert (UX gating only; the
 * server decides). Acknowledging is one click; resolving opens a prompt for the note the server requires; reassigning is chosen in
 * a popover and reported here so the toast names who has the alert now.
 */
export function useRaisedAlerts() {
  const alerts = useRaisedAlertList();
  const toast = useToast();
  const { state } = useCurrentUser();
  const connection = useAlertFeed(alerts.refresh);
  const now = useMinuteClock();
  const [answeringId, setAnsweringId] = useState<string | null>(null);
  const [resolving, setResolving] = useState<AlertView | null>(null);
  const permissions = state.status === 'authenticated' ? state.user.permissions : [];
  const canRespond = permissions.includes('alert.respond');
  const canAssign = permissions.includes('alert.assign');

  async function acknowledge(alert: AlertView) {
    setAnsweringId(alert.id);
    try {
      await acknowledgeAlert(alert.id);
      toast.success(`${alert.ruleName} on ${alert.truckRegistration} acknowledged`);
      await alerts.reload();
    } catch {
      toast.error("Couldn't acknowledge the alert. Someone may have answered it already; reload to see.");
    } finally {
      setAnsweringId(null);
    }
  }

  /** A throw stays with the modal, which shows it inline. */
  async function resolve(alert: AlertView, note: string) {
    await resolveAlert(alert.id, { note });
    toast.success(`${alert.ruleName} on ${alert.truckRegistration} resolved`);
    await alerts.reload();
  }

  /** A throw stays with the popover, which shows it inline. */
  async function reassign(alert: AlertView, assignee: { id: string; name: string }, note?: string) {
    await reassignAlert(alert.id, { assigneeId: assignee.id, note });
    toast.success(`Reassigned ${alert.ruleName} on ${alert.truckRegistration} to ${assignee.name}`);
    await alerts.reload();
  }

  const answer = (alert: AlertView, action: 'acknowledge' | 'resolve') => (action === 'acknowledge' ? acknowledge(alert) : setResolving(alert));

  return { ...alerts, connection, now, canRespond, canAssign, answeringId, answer, reassign, resolving, resolve, closeResolving: () => setResolving(null) };
}
